import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { errorText, useApi } from '../lib/api';
import {
  deleteConfirmText,
  loadConfirmText,
  RESET_TITLES,
  resetConfirmText,
  TesterToolsMissing,
  undoConfirmText,
  type DevSaveLoadReport,
  type DevSaveRow,
  type ResetMode,
} from '../lib/devSaves';
import {
  createSave,
  deleteSave,
  exportSave,
  isNameTaken,
  listSaves,
  loadSave,
  resetPlayer,
  undoPlayer,
} from '../lib/devSavesApi';
import { LoadReportView, SaveTable, saveBtn } from './SaveTable';

const input = 'rounded border-2 border-ink bg-white px-2 py-1';

/**
 * Save points on the player detail (tester tools): save now, reset to day 1 / new run, undo the last load or reset,
 * and this player's saves (taken from them or automatically for them) with load, export and delete. A load, reset or
 * undo replaces the player's whole state, so every query is refetched and the report is shown.
 */
export function PlayerSavePoints({
  playerId,
  playerLabel,
}: {
  playerId: string;
  playerLabel: string;
}) {
  const { client, session } = useApi();
  const qc = useQueryClient();
  const saves = useQuery({
    queryKey: ['dev-saves', 'player', playerId],
    queryFn: () => listSaves(client, { playerId, kind: 'all' }),
    retry: (n, e) => !(e instanceof TesterToolsMissing) && n < 2,
  });

  const [form, setForm] = useState({ name: '', description: '' });
  const [problem, setProblem] = useState<string | null>(null);
  const [report, setReport] = useState<{ title: string; report: DevSaveLoadReport } | null>(null);

  const refreshAll = () => void qc.invalidateQueries();
  const refreshSaves = () => void qc.invalidateQueries({ queryKey: ['dev-saves'] });

  const save = useMutation({
    mutationFn: async (overwrite: boolean) =>
      createSave(client, {
        playerId,
        name: form.name.trim(),
        description: form.description.trim() || null,
        overwrite,
      }),
    onSuccess: () => {
      setProblem(null);
      setForm({ name: '', description: '' });
      refreshSaves();
    },
    onError: (e, overwrite) => {
      if (!overwrite && isNameTaken(e)) {
        if (confirm(`A save named "${form.name.trim()}" already exists. Overwrite it?`))
          save.mutate(true);
        return;
      }
      setProblem(errorText(e));
    },
  });

  const action = useMutation({
    mutationFn: async (a: { title: string; run: () => Promise<DevSaveLoadReport> }) => ({
      title: a.title,
      report: await a.run(),
    }),
    onSuccess: (r) => {
      setProblem(null);
      setReport(r);
      refreshAll();
    },
    onError: (e) => setProblem(errorText(e)),
  });

  const remove = useMutation({
    mutationFn: (s: DevSaveRow) => deleteSave(client, s.id),
    onSuccess: () => {
      setProblem(null);
      refreshSaves();
    },
    onError: (e) => setProblem(errorText(e)),
  });

  const download = useMutation({
    mutationFn: (s: DevSaveRow) => exportSave(client, s),
    onError: (e) => setProblem(errorText(e)),
  });

  if (saves.error instanceof TesterToolsMissing) {
    return (
      <section className="rounded border-2 border-ink bg-white p-4 lg:col-span-2">
        <h2 className="font-semibold">Save points</h2>
        <p className="text-sm text-bruise">{saves.error.message}</p>
      </section>
    );
  }

  const reset = (mode: ResetMode) => {
    if (!confirm(resetConfirmText(mode, playerLabel))) return;
    action.mutate({
      title: RESET_TITLES[mode],
      run: () => resetPlayer(client, playerId, mode),
    });
  };
  const undo = () => {
    if (!confirm(undoConfirmText(playerLabel))) return;
    action.mutate({ title: 'Undo', run: () => undoPlayer(client, playerId) });
  };
  const load = (s: DevSaveRow) => {
    if (!confirm(loadConfirmText(s, playerLabel, s.sourcePlayerId === playerId))) return;
    action.mutate({ title: `Loaded "${s.name}"`, run: () => loadSave(client, s.id, playerId) });
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return setProblem('A name is required.');
    save.mutate(false);
  };
  const busy = action.isPending || save.isPending;

  return (
    <section className="overflow-x-auto rounded border-2 border-ink bg-white p-4 lg:col-span-2">
      <h2 className="font-semibold">Save points</h2>
      <p className="text-xs text-bruise">
        Tester tools. A save covers everything this player owns; loading, resetting or undoing
        replaces it all (an automatic save is taken first).
        {!session.isAdmin && ' This is for Admins.'}
      </p>
      <form onSubmit={submit} className="mt-2 flex flex-wrap items-center gap-2 text-sm">
        <input
          aria-label="Save name"
          placeholder="name (required)"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className={`${input} w-56`}
        />
        <input
          aria-label="Save description"
          placeholder="description (optional)"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className={`${input} w-80 max-w-full`}
        />
        <button
          type="submit"
          disabled={busy}
          className="rounded border-2 border-ink bg-caption px-3 py-1 font-semibold disabled:opacity-50"
        >
          Save now
        </button>
      </form>
      <div className="mt-2 flex flex-wrap gap-2 text-sm">
        <button
          type="button"
          disabled={busy}
          onClick={() => reset('day1')}
          className="rounded border-2 border-ink bg-blam px-3 py-1 font-semibold text-white disabled:opacity-50"
        >
          Reset to day 1
        </button>
        {(['newCharacter', 'kill', 'resurrect', 'tutorialFresh', 'movementPassed', 'graduated'] as const).map((mode) => (
          <button
            key={mode}
            type="button"
            disabled={busy}
            onClick={() => reset(mode)}
            className="rounded border-2 border-ink bg-blam px-3 py-1 font-semibold text-white disabled:opacity-50"
          >
            {RESET_TITLES[mode]}
          </button>
        ))}
        <button
          type="button"
          disabled={busy}
          onClick={undo}
          className="rounded border-2 border-ink px-3 py-1 font-semibold disabled:opacity-50"
        >
          Undo last load/reset
        </button>
      </div>
      {problem && (
        <p className="mt-2 text-sm text-blam" role="alert">
          {problem}
        </p>
      )}
      {report && (
        <LoadReportView
          title={report.title}
          report={report.report}
          onClose={() => setReport(null)}
        />
      )}
      {saves.isPending ? (
        <p className="mt-2 text-sm">Loading&hellip;</p>
      ) : saves.isError ? (
        <p className="mt-2 text-sm text-blam">{errorText(saves.error)}</p>
      ) : (
        <SaveTable
          rows={saves.data.items}
          actions={(s) => (
            <>
              <button type="button" className={saveBtn} disabled={busy} onClick={() => load(s)}>
                Load onto this player
              </button>
              <button
                type="button"
                className={saveBtn}
                disabled={download.isPending}
                onClick={() => download.mutate(s)}
              >
                Export
              </button>
              <button
                type="button"
                className={saveBtn}
                disabled={remove.isPending}
                onClick={() => confirm(deleteConfirmText(s)) && remove.mutate(s)}
              >
                Delete
              </button>
            </>
          )}
        />
      )}
    </section>
  );
}
